import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import {
  ISubscription,
  EBillingFrequency,
} from 'app/store/models/subscription.model';
import { ISelectItem } from 'app/store/models/select.model';
import { ECampaignType } from 'app/store/models/campaigns.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { SubscriptionService } from 'app/store/services/subscription.service';
import { MatRadioChange } from '@angular/material/radio';
import { Store } from '@ngrx/store';
import { IReducers } from 'app/store/reducers';
import { openDialog } from 'app/store/actions/page.action';
import { ChooseFrequencyComponent } from 'app/dialogs/choose-frequency/choose-frequency.component';

@Component({
  selector: 'app-edit-subscription',
  templateUrl: './edit-subscription.component.html',
  styleUrls: ['./edit-subscription.component.scss'],
})
export class EditSubscriptionComponent implements OnInit {
  campaignTypeItems: ISelectItem[] = [
    {
      label: 'Standard',
      value: ECampaignType.STANDARD,
    },
    {
      label: 'Frequency',
      value: ECampaignType.FREQUENCY,
    },
  ];

  billingFrequencyItems: ISelectItem[] = [
    {
      label: 'Monthly',
      value: EBillingFrequency.MONTHLY,
    },
    {
      label: 'Quarterly',
      value: EBillingFrequency.QUARTERLY,
    },
    {
      label: 'Semi-Annualy',
      value: EBillingFrequency.SEMI_ANNUALY,
    },
    {
      label: 'Annualy',
      value: EBillingFrequency.ANNUALY,
    },
  ];

  saveButtonProperties: Button = {
    color: ButtonColor.BLUE,
    size: ButtonSize.SMALL,
    onClick: () => this.submitForm(),
    text: 'Edit',
    style: 'outline',
  };

  cancelButtonProperties: Button = {
    color: ButtonColor.RED,
    size: ButtonSize.SMALL,
    text: 'Cancel',
    route: ['/subscriptions'],
    style: 'outline',
  };

  state: 'edit' | 'new';

  formGroup: FormGroup;

  subscription: ISubscription;

  submitForm() {
    if (this.formGroup.valid) {
      if (this.state === 'edit') {
        this.subscriptionService
          .updateSubscription(this.subscription.id, this.formGroup.value)
          .subscribe(res => this.router.navigate(['/subscriptions']));
      } else {
        this.subscriptionService
          .createSubscription(this.formGroup.value)
          .subscribe(res => this.router.navigate(['/subscriptions']));
      }
    } else {
      this.formGroup.markAllAsTouched();
    }
  }

  onCampaignTypeChange(event: MatRadioChange, controlType: string) {
    if (event.value === ECampaignType.FREQUENCY) {
      this.store.dispatch(
        openDialog({
          dialogData: {
            content: ChooseFrequencyComponent,
            title: 'Choose Frequency',
            canClose: true,
            dialogMinWidth: '400px',
            buttons: [
              {
                color: ButtonColor.BLUE,
                size: ButtonSize.SMALL,
                actAsClose: true,
                text: 'Choose',
                onClick: dialog =>
                  this.formGroup
                    .get(controlType)
                    .setValue(dialog.dialogForm.get('frequency').value),
              },
            ],
          },
        })
      );
    }
  }

  getButtonTitle(): string {
    return this.state === 'edit' ? 'Update' : 'Create';
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private subscriptionService: SubscriptionService,
    private store: Store<IReducers>
  ) {}

  ngOnInit() {
    this.state = this.activatedRoute.snapshot.data.state;

    if (this.state === 'edit') {
      const subscriptionId = this.activatedRoute.snapshot.params.id;
      this.subscriptionService
        .viewSubscription(subscriptionId)
        .subscribe((subscription: ISubscription) => {
          this.subscription = subscription;
          this.initForm(subscription);
        });
    } else {
      this.initForm();
    }
  }

  initForm(subscriptionItem?: ISubscription) {
    this.formGroup = new FormGroup({
      team_code: new FormControl(
        subscriptionItem ? subscriptionItem.team_code : '',
        [Validators.required]
      ),
      plan_id: new FormControl(
        subscriptionItem ? subscriptionItem.plan_id : ''
      ),
      plan_cost: new FormControl(
        subscriptionItem ? subscriptionItem.plan_cost : '',
        [Validators.required]
      ),
      trial_days: new FormControl(
        subscriptionItem ? subscriptionItem.trial_days : ''
      ),
      coupon_id: new FormControl(
        subscriptionItem ? subscriptionItem.coupon_id : ''
      ),
      subscription_cost: new FormControl(
        subscriptionItem ? subscriptionItem.subscription_cost : ''
      ),
      credits: new FormControl(
        subscriptionItem ? subscriptionItem.credits : ''
      ),
      content_code: new FormControl(
        subscriptionItem ? subscriptionItem.content_code : ''
      ),
      campaign_name: new FormControl(
        subscriptionItem ? subscriptionItem.campaign_name : ''
      ),
      campaign_type: new FormControl(
        subscriptionItem ? subscriptionItem.campaign_type : ''
      ),
      frequency: new FormControl(
        subscriptionItem ? subscriptionItem.frequency : ''
      ),
      frequency2: new FormControl(
        subscriptionItem ? subscriptionItem.frequency2 : ''
      ),
      frequency3: new FormControl(
        subscriptionItem ? subscriptionItem.frequency3 : ''
      ),
      user_contact_group: new FormControl(
        subscriptionItem ? subscriptionItem.user_contact_group : ''
      ),
      content_code2: new FormControl(
        subscriptionItem ? subscriptionItem.content_code2 : ''
      ),
      campaign_name2: new FormControl(
        subscriptionItem ? subscriptionItem.campaign_name2 : ''
      ),
      campaign_type2: new FormControl(
        subscriptionItem ? subscriptionItem.campaign_type2 : ''
      ),
      user_contact_group2: new FormControl(
        subscriptionItem ? subscriptionItem.user_contact_group2 : ''
      ),
      content_code3: new FormControl(
        subscriptionItem ? subscriptionItem.content_code3 : ''
      ),
      campaign_name3: new FormControl(
        subscriptionItem ? subscriptionItem.campaign_name3 : ''
      ),
      campaign_type3: new FormControl(
        subscriptionItem ? subscriptionItem.campaign_type3 : ''
      ),
      user_contact_group3: new FormControl(
        subscriptionItem ? subscriptionItem.user_contact_group3 : ''
      ),
      billing_frequency: new FormControl(
        subscriptionItem ? subscriptionItem.billing_frequency : null
      ),
      additional_user_cost: new FormControl(
        subscriptionItem ? subscriptionItem.additional_user_cost : ''
      ),
      additional_user_plan: new FormControl(
        subscriptionItem ? subscriptionItem.additional_user_plan : ''
      ),
    });
  }
}
